import * as Yup from 'yup';
import { App } from 'App';
import { AppRegistrationSection } from 'AppRegistrationSection';
import { ButtonActor, InputActor } from 'components';
import { datadogLogs } from '@datadog/browser-logs';
import { dniRegExp, passwordRegExp } from 'utils/regularExpressions';
import { FormWarning } from 'components/molecules';
import { Frame } from 'components/organisms';
import { frameConfig } from 'AppConfig';
import { getAmountAndTermFromConstraints } from 'hooks/useApplicationSummary';
import { getClientProductNumber } from 'utils/getProductNumber';
import { getFetchHeaders } from 'utils/fetchHeaders';
import { getScreenTranslation } from 'utils/translations';
import {
  JUICY_NEXT_BTN,
  LOGIN_ROUTE,
  SESSION_MOBILE_INSTANCE_ID,
} from 'utils/constants';
import { LockIcon, ShieldIcon } from 'components/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import { routes } from '../../routes';
import { Row, Spacer } from 'components/atoms';
import { TestingContext } from 'InternalLibrary/testing';
import { useAnalytics } from 'use-analytics';
import { useAnalyticsCookies } from 'hooks/useAnalyticsCookies';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useErrorManager } from 'InternalLibrary/app';
import { useFormik } from 'formik';
import {
  useGetClient,
  useGetClientApplication,
  useGetClientEmail,
  usePostLeadsRegister,
} from '../../api/atoms';
import { useMaybeNavigateByResolution } from '../../hooks/useMaybeNavigateByResolution';

import { useConstraints } from 'hooks/useConstraints';
import { useOffer } from 'hooks/useOffer';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import FieldGroup from 'components/atoms/FieldGroup/FieldGroup';
import layout from 'scss/layout.module.scss';
import styles from './Confirmation.module.scss';
import Text from 'components/atoms/Typography/Text';
import Title from 'components/atoms/Title/Title';
import useAffiliateInfo from 'hooks/useAffiliateInfo';
import useAuth from 'hooks/useAuth';
import useCrossDomainLoginRedirect from 'hooks/useCrossDomainLoginRedirect';
import useIovation, { ioBlackBoxAtom } from 'hooks/useIovation';

export default function Confirmation() {
  const { login } = useAuth();
  const { setError } = useErrorManager();
  const apiLeadsRegister = usePostLeadsRegister();
  const apiOffer = useOffer();
  const apiClient = useGetClient();
  const apiConstraints = useConstraints();
  const { fetch: fetchGetClient } = useGetClient();
  const apiClientEmail = useGetClientEmail();
  const { fetch: fetchGetClientApplication } = useGetClientApplication();
  const { fetch: fetchPostOnboardingRegister } = App.useApi(
    'onboarding/register',
    'POST',
  );
  const { maybeNavigateByResolution } = useMaybeNavigateByResolution();
  const navigate = useNavigate();
  const { maybeRedirectCrossDomain } = useCrossDomainLoginRedirect();
  const ioBlackBox = useRecoilValue(ioBlackBoxAtom);
  const [loading, setLoading] = useState(false);
  const fieldMatch: Record<string, string> = {
    email: 'id',
    personalId: 'id',
    mobilePhone: 'id',
  };
  const { track } = useAnalytics();
  const { setTestingData } = useContext(TestingContext);
  const { getAnalyticsCookies } = useAnalyticsCookies();
  const i18next = useTranslation();

  const onSubmit = async (data: any) => {
    try {
      const applicationInstanceId = sessionStorage.getItem(
        SESSION_MOBILE_INSTANCE_ID,
      );

      const { clientId } = await fetchPostOnboardingRegister({
        body: {
          personalId: data.id,
          password: data.password,
          passwordRepeat: data.password,
          email: leads?.email,
          mobilePhone: leads?.mobilePhone,
          source: 'DESKTOP',
          acceptAgreement: leads?.acceptAgreement,
          acceptNews: leads?.acceptNews,
          acceptDataSharing: leads?.acceptProfiling,
          productNumber: App.config.productNumber,
          ioBlackBox,
          affiliateInfo: affiliateInfo.provider ? affiliateInfo : undefined,
          analyticsCookies: getAnalyticsCookies(),
          applicationInstanceId,
        },
      });

      setTestingData({
        clientID: String(clientId),
      });
      datadogLogs.setUser({
        id: clientId?.toString(),
      });

      let queryParameters = apiOffer.request?.queryParameters;
      if (queryParameters === undefined) {
        const constraints = await apiConstraints.fetch({
          headers: getFetchHeaders(),
          pathParameters: {
            productNumber: getClientProductNumber(
              apiClient.data?.availableProducts,
            ),
          },
        });
        queryParameters = getAmountAndTermFromConstraints(constraints);
      }
      await login(data.id, data.password);

      const client = await fetchGetClient({
        headers: getFetchHeaders(),
      });
      const email = await apiClientEmail.fetch({
        headers: getFetchHeaders(),
      });

      track('identify', {
        client,
        email: email.email,
      });

      const application = await fetchGetClientApplication({
        headers: getFetchHeaders({
          Accept: 'application/vnd.4finance.web.v1.hal+json',
          'Content-Type': 'application/vnd.4finance.web.v1.hal+json',
        }),
      });

      // Redirects to either vivusonline registration or account
      if (maybeRedirectCrossDomain(client, application)) {
        return;
      }

      if (application.status) {
        track('identify', {
          application,
        });
      }

      if (await maybeNavigateByResolution({ application, client })) {
        return;
      }

      if (queryParameters !== undefined) {
        await apiOffer.fetch({
          headers: getFetchHeaders(),
          pathParameters: {
            productNumber: getClientProductNumber(
              apiClient.data?.availableProducts,
            ),
          },
          queryParameters,
        });
      }
      sessionStorage.removeItem('lead');
      navigate(routes.FirstLoan_PersonalData);
    } catch (error) {
      datadogLogs.logger.error(
        `Client account registration failed! - error: ${JSON.stringify(
          error,
          null,
          2,
        )}`,
      );

      // TODO: use addErrors instead.
      // addErrors
      // removeErrors
      if (error.name === 'ValidationError') {
        error.fieldErrors.forEach(
          (error: { messageTemplate: string; property: string }) => {
            if (error.messageTemplate === 'already_exists_in_db') {
              formik.setStatus({
                errors: {
                  _submit: [
                    <FormWarning
                      text={i18next.t(
                        'notification:loginRegistrationError.text',
                        {
                          login: LOGIN_ROUTE,
                          phone: i18next.t('common:contactPhone'),
                          'phone-no-spaces': i18next
                            .t('common:contactPhone')
                            .replace(/\s/g, ''),
                        },
                      )}
                      title={i18next.t(
                        'notification:loginRegistrationError.title',
                      )}
                    />,
                  ],
                },
              });
            } else {
              formik.setStatus({
                errors: {
                  [fieldMatch[error.property]]: [
                    i18next.t(
                      `notification:validation.${error.messageTemplate}.${error.property}`,
                    ),
                  ],
                },
              });
            }
          },
        );
      } else {
        setError(error);
      }
    }
  };

  const content = getScreenTranslation('confirmation', i18next);
  const fieldGroupProps = {
    validationTextClassName: styles.validationLabel,
  };

  const ValidationSchema = Yup.object().shape({
    id: Yup.string()
      .matches(dniRegExp, i18next.t('validation:wrong_format'))
      .required(i18next.t('validation:required_field')),
    password: Yup.string()
      .required(i18next.t('validation:required_field'))
      .min(5, i18next.t('validation:too_short'))
      .matches(passwordRegExp, i18next.t('validation:weak_password')),
  });

  const formik = useFormik({
    initialValues: {
      id: '',
      password: '',
    },
    validationSchema: ValidationSchema,
    onSubmit: async (data) => {
      formik.setStatus({ errors: {} });
      setLoading(true);
      await onSubmit(data);
      setLoading(false);
    },
  });

  useEffect(() => {
    setTestingData({});
  }, []);

  useIovation();

  const { affiliateInfo } = useAffiliateInfo();

  const leads = useMemo(() => {
    const apiLeadsRequestBody = apiLeadsRegister.request?.body;
    if (apiLeadsRequestBody) {
      return apiLeadsRequestBody;
    }

    const ssLeadStr = sessionStorage.getItem('lead');
    if (!ssLeadStr) {
      return null;
    }
    const { email, mobilePhone, agreements } = JSON.parse(ssLeadStr);

    return {
      email,
      mobilePhone,
      acceptNews: agreements?.firstAgreement,
      acceptProfiling: agreements?.secondAgreement,
      acceptAgreement: agreements?.thirdAgreement,
    };
  }, [apiLeadsRegister.request?.body]);

  if (
    !leads?.email ||
    !leads?.mobilePhone ||
    leads?.acceptAgreement === undefined ||
    leads?.acceptNews === undefined ||
    leads?.acceptProfiling === undefined
  ) {
    return <Navigate to={routes.FirstLoan_Email} />;
  }

  return (
    <Frame
      {...frameConfig}
      hideLogout
      sectionID={AppRegistrationSection.Identification}
    >
      <div className={layout['width-sm']}>
        <Spacer marginBottom="40px">
          <Title text={content.title} data-test="css-Create-Account-Title" />
        </Spacer>

        <form onSubmit={formik.handleSubmit}>
          <Spacer marginBottom="30px">
            <FieldGroup helperText={content.idInputLabel} {...fieldGroupProps}>
              <InputActor
                className={styles.border}
                name="id"
                label={content.idPlaceholder}
                inputProps={{
                  type: 'text',
                  dataTest: 'id-Id',
                }}
                formik={formik}
                sanitizer={(value: any) => value.toUpperCase().trim()}
                fieldGroupProps={{
                  dataTestValidationText: 'idInputValidationText',
                  ...fieldGroupProps,
                }}
                analytics={{
                  description: 'Personal ID (DNI/NIE)',
                }}
              />
            </FieldGroup>
          </Spacer>
          <Spacer marginBottom="30px">
            <FieldGroup
              helperText={content.passwordInputLabel}
              {...fieldGroupProps}
            >
              <InputActor
                className={styles.border}
                name="password"
                label={content.passwordPlaceholder}
                inputProps={{
                  type: 'password',
                  dataTest: 'id-Password',
                }}
                formik={formik}
                analytics={{
                  dontTrackContent: true,
                  description: 'Password',
                }}
                fieldGroupProps={{
                  dataTestValidationText: 'passwordInputValidationText',
                  ...fieldGroupProps,
                }}
              />
            </FieldGroup>
          </Spacer>
          <ButtonActor
            buttonProps={{
              className: JUICY_NEXT_BTN,
              leftIcon: <LockIcon />,
              type: 'submit',
              fill: true,
              loading,
              dataTest: 'xpath-Create-Account-Button',
            }}
            formik={formik}
            analytics={{
              description: 'Continue button',
            }}
          >
            {content.continueButtonText}
          </ButtonActor>
        </form>
        <Spacer marginTop="30px">
          <Row>
            <div className={styles['shield-icon-wrapper']}>
              <ShieldIcon className="test" />
            </div>
            <Text
              text={content.details}
              kind="p"
              level={3}
              className={styles['bottom-helper-text']}
              data-test="css-Secure-Info"
            />
          </Row>
        </Spacer>
      </div>
    </Frame>
  );
}
